import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  PortalDsButton,
  PortalDsCard,
  portalDs,
} from 'src/components/portal-design-system';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'src/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from 'src/components/ui/dialog';
import { cn } from 'src/lib/utils';
import { useActiveClient } from 'src/redux/active-client/use-active-client';
import notificationService from 'src/services/notification/notification.service';
import { ClientDetailSection } from '../../utils/client-detail-url.utils';
import { useUnsavedChangesGuard } from '../../hooks/use-unsaved-changes-guard';
import ManageClientDrawerHeader from './manage-client-drawer-header';
import ManageClientPermissions from './manage-client-permissions';
import ManageClientAccess from './manage-client-access';
import ManageClientUsers from './manage-client-users';
import ManageClientViewsTemp from './manage-client-views-temp';
import ManageClientStudioDashboard from './manage-client-studio-dashboard';
import DeleteClientModal from './delete-client-modal';
import MigrateClientModal from './migrate-client-modal';
import ClientStudioSection from './client-studio-section';
import { Client, ClientStudioSyncResponse } from '../../types/client.types';
import { isClientWebsiteMigratable, STUDIO_MIGRATION_WEBSITE_REQUIRED_MESSAGE } from '../../utils/client-url.utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';

interface ManageClientDrawerProps {
  open: boolean;
  section: ClientDetailSection;
  onSectionChange: (section: ClientDetailSection) => void;
  onOpenChange: (open: boolean) => void;
}

const DETAIL_TABS: { id: ClientDetailSection; label: string }[] = [
  { id: 'access', label: 'Access' },
  { id: 'views', label: 'Reporting' },
  { id: 'permissions', label: 'Permissions' },
  { id: 'users', label: 'Users' },
];

const TAB_TITLES: Record<ClientDetailSection, string> = {
  access: 'Access',
  views: 'Reporting',
  permissions: 'Permissions',
  users: 'Users',
};

const TAB_TRIGGER_CLASS =
  'rounded-none border-b-2 border-transparent bg-transparent px-0 pb-3 text-[14px] font-semibold text-[#5E6278] shadow-none hover:bg-transparent hover:text-[#0D0F1A] [&[aria-selected=true]]:border-[#0D0F1A] [&[aria-selected=true]]:bg-transparent [&[aria-selected=true]]:text-[#0D0F1A] [&[aria-selected=true]]:shadow-none';

export const ManageClientDrawer: React.FC<ManageClientDrawerProps> = ({
  open,
  section,
  onSectionChange,
  onOpenChange,
}) => {
  const { activeClient, clearClient, setClient } = useActiveClient();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [activeViewType, setActiveViewType] = useState<'old' | 'new'>('old');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showMigrateModal, setShowMigrateModal] = useState(false);
  const [headerActionsEl, setHeaderActionsEl] = useState<HTMLDivElement | null>(null);
  const [accessDirty, setAccessDirty] = useState(false);
  const {
    showDialog: showUnsavedDialog,
    requestNavigation,
    confirmDiscard,
    cancelDiscard,
  } = useUnsavedChangesGuard(accessDirty);

  const handleAccessDirtyChange = useCallback((dirty: boolean) => {
    setAccessDirty(dirty);
  }, []);

  const handleClose = () => {
    requestNavigation(() => onOpenChange(false));
  };

  const handleSectionChange = (nextSection: ClientDetailSection) => {
    if (nextSection === section) {
      return;
    }
    requestNavigation(() => onSectionChange(nextSection));
  };

  const handlePreview = () => {
    if (!activeClient?.ext_client_id) {
      return;
    }
    const previewUrl = `/reports-old?previewClientId=${activeClient.ext_client_id}&previewClientName=${activeClient.title}`;
    requestNavigation(() => navigate(previewUrl));
  };

  const handleClientDeleted = () => {
    clearClient();
    onOpenChange(false);
    notificationService.handleSuccess(null, dispatch, 'Client deleted successfully');
  };

  const handleClientMigrated = (result: ClientStudioSyncResponse) => {
    if (!activeClient) {
      return;
    }
    setClient({
      ...activeClient,
      studio_ready: true,
      studio_migrated_at: result.studio_migrated_at ?? activeClient.studio_migrated_at,
      self_service_workspace_id:
        result.workspace_id ?? activeClient.self_service_workspace_id,
      studio_sync_last_error: null,
      studio_sync_last_success_at: result.studio_migrated_at
        ?? activeClient.studio_sync_last_success_at
        ?? new Date().toISOString(),
      studio_sync_last_attempt_at: new Date().toISOString(),
    });
  };

  const isMigrated = Boolean(activeClient?.studio_migrated_at);
  const canMigrateToStudio = activeClient
    ? isClientWebsiteMigratable(activeClient.website)
    : false;

  if (!open) {
    return null;
  }

  return (
    <>
      <div className={cn(portalDs.page, 'h-full overflow-y-auto')}>
        <div className="mx-auto w-full max-w-[1360px] pb-16">
          <div className="mb-5 flex items-center justify-between gap-4">
            <PortalDsButton
              variant="ghost"
              size="sm"
              onClick={handleClose}
            >
              ‹ Back to clients
            </PortalDsButton>
            <div className="flex flex-col items-end gap-1">
              <div className="flex gap-2">
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex">
                        <PortalDsButton
                          variant="secondary"
                          size="sm"
                          disabled={!canMigrateToStudio}
                          onClick={() => setShowMigrateModal(true)}
                        >
                          {isMigrated ? 'Re-sync Studio' : 'Migrate to Studio'}
                        </PortalDsButton>
                      </span>
                    </TooltipTrigger>
                    {!canMigrateToStudio ? (
                      <TooltipContent>
                        {STUDIO_MIGRATION_WEBSITE_REQUIRED_MESSAGE}
                      </TooltipContent>
                    ) : null}
                  </Tooltip>
                </TooltipProvider>
                <PortalDsButton
                  variant="dangerGhost"
                  size="sm"
                  onClick={() => setShowDeleteModal(true)}
                >
                  Delete client
                </PortalDsButton>
              </div>
              {!canMigrateToStudio ? (
                <p className={portalDs.hint}>
                  {STUDIO_MIGRATION_WEBSITE_REQUIRED_MESSAGE}
                </p>
              ) : null}
            </div>
          </div>

          <ManageClientDrawerHeader />

          {activeClient ? (
            <ClientStudioSection
              client={activeClient}
              drawerOpen={open}
            />
          ) : null}

          <Tabs
            value={section}
            onValueChange={(value) => handleSectionChange(value as ClientDetailSection)}
            className="min-w-0"
          >
            <TabsList className="mb-6 h-auto rounded-none bg-transparent p-0">
              {DETAIL_TABS.map((tab, index) => (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id}
                  className={cn(TAB_TRIGGER_CLASS, index < DETAIL_TABS.length - 1 && 'mr-8')}
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>

            <PortalDsCard
              padding={false}
              className="overflow-hidden"
            >
              <div className="flex items-center justify-between gap-4 border-b border-[#EDEEF2] px-5 py-4 sm:px-6">
                <h2 className="text-base font-semibold tracking-[-0.2px] text-[#0D0F1A]">
                  {TAB_TITLES[section]}
                </h2>
                <div className="flex flex-wrap items-center gap-2">
                  {section === 'views' && activeViewType === 'old' ? (
                    <PortalDsButton
                      variant="secondary"
                      size="sm"
                      onClick={handlePreview}
                    >
                      Preview
                    </PortalDsButton>
                  ) : null}
                  <div
                    ref={setHeaderActionsEl}
                    className="flex items-center gap-2"
                  />
                </div>
              </div>

              <div className="p-5 sm:p-6">
                <TabsContent
                  value="access"
                  className="mt-0"
                >
                  <ManageClientAccess
                    headerActionsEl={headerActionsEl}
                    onDirtyChange={handleAccessDirtyChange}
                  />
                </TabsContent>
                <TabsContent
                  value="views"
                  className="mt-0"
                >
                  <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                    <p className={portalDs.bodySm}>
                      Build the dashboard this client sees when they log in.
                    </p>
                    <div className={portalDs.segmentGroup}>
                      <button
                        type="button"
                        className={cn(
                          portalDs.segment,
                          activeViewType === 'old' && portalDs.segmentActive
                        )}
                        onClick={() => setActiveViewType('old')}
                      >
                        Legacy
                      </button>
                      <button
                        type="button"
                        className={cn(
                          portalDs.segment,
                          activeViewType === 'new' && portalDs.segmentActive
                        )}
                        onClick={() => setActiveViewType('new')}
                      >
                        Studio
                      </button>
                    </div>
                  </div>
                  {activeViewType === 'old' && (
                    <ManageClientViewsTemp
                      client={activeClient ?? {} as Client}
                      isOpen={open}
                      onClose={() => onOpenChange(false)}
                    />
                  )}
                  {activeViewType === 'new' && activeClient ? (
                    <ManageClientStudioDashboard
                      key={activeClient.ext_client_id}
                      client={activeClient}
                      headerActionsEl={headerActionsEl}
                      onPreview={handlePreview}
                    />
                  ) : null}
                </TabsContent>
                <TabsContent
                  value="permissions"
                  className="mt-0"
                >
                  <ManageClientPermissions
                    permissions={activeClient?.permissions}
                    headerActionsEl={headerActionsEl}
                    onOpenChange={onOpenChange}
                  />
                </TabsContent>
                <TabsContent
                  value="users"
                  className="mt-0"
                >
                  <ManageClientUsers
                    showAddUser={false}
                    showInviteUser={false}
                    onAddUser={() => undefined}
                    onInviteUser={() => undefined}
                  />
                </TabsContent>
              </div>
            </PortalDsCard>
          </Tabs>
        </div>
      </div>

      <DeleteClientModal
        open={showDeleteModal}
        client={activeClient}
        onOpenChange={setShowDeleteModal}
        onDeleted={handleClientDeleted}
      />
      <MigrateClientModal
        open={showMigrateModal}
        client={activeClient}
        onOpenChange={setShowMigrateModal}
        onMigrated={handleClientMigrated}
      />
      <Dialog
        open={showUnsavedDialog}
        onOpenChange={(open) => {
          if (!open) {
            cancelDiscard();
          }
        }}
      >
        <DialogContent
          className="sm:max-w-md bg-white"
          onPointerDownOutside={(event) => event.preventDefault()}
        >
          <DialogTitle className={portalDs.sectionTitle}>
            Unsaved changes
          </DialogTitle>
          <p className={portalDs.sheetDescription}>
            You have unsaved access changes. If you leave, they will be lost.
          </p>
          <DialogFooter className="gap-2 sm:gap-0">
            <PortalDsButton
              variant="secondary"
              size="sm"
              type="button"
              onClick={cancelDiscard}
            >
              Stay
            </PortalDsButton>
            <PortalDsButton
              variant="primary"
              size="sm"
              type="button"
              onClick={confirmDiscard}
            >
              Leave without saving
            </PortalDsButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ManageClientDrawer;
